import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, QUERY_DEFAULTS } from '@/shared/lib/constants';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { domainKeys, userKeys } from '@/shared/lib/query/queryKeys';

import type { DomainItem, DomainStakeholder } from '@/entities/domain';

import { fetchDomainUsers, updateDomainExperts } from '@/features/domain/api';
import type { DomainUser } from '@/features/domain/api';

type UseDomainExpertEditProps = {
  domainId: string;
  domainFqn: string;
};

export type ExpertEditHook = {
  isOpen: boolean;
  selectedUserIds: string[];
  users: DomainUser[];
  currentExperts: DomainStakeholder[];
  isLoadingUsers: boolean;
  isUpdating: boolean;
  hasChanges: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  handleUserSelect: (userId: string, checked: boolean) => void;
  handleUpdate: () => void;
};

export const useDomainUsers = () => {
  return useQueryWithBoundary({
    queryKey: userKeys.domainUsers(),
    queryFn: () => fetchDomainUsers(25),
    staleTime: QUERY_DEFAULTS.STALE_TIME.LONG,
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useDomainExpertEdit = ({ domainId, domainFqn }: UseDomainExpertEditProps): ExpertEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const domainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
  const currentExperts = useMemo(() => domainData?.experts || [], [domainData?.experts]);

  const { data: users = [], isLoading: isLoadingUsers } = useDomainUsers();

  const updateExpertsMutation = useMutation({
    mutationFn: async ({
      selectedUserIds,
      users,
      currentExperts,
    }: {
      selectedUserIds: string[];
      users: DomainUser[];
      currentExperts: DomainStakeholder[];
    }) => {
      await updateDomainExperts(domainId, selectedUserIds, users, currentExperts);
    },
    onMutate: async ({ selectedUserIds, users }) => {
      await queryClient.cancelQueries({ queryKey: domainKeys.detail(domainFqn) });
      const previousDomain = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));

      if (previousDomain) {
        const newExperts: DomainStakeholder[] = selectedUserIds.map((userId) => {
          const user = users.find((u) => u.id === userId);
          if (!user) {
            throw new Error(`User with id ${userId} not found`);
          }
          return {
            id: user.id,
            type: 'user',
            name: user.name,
            fullyQualifiedName: user.fullyQualifiedName,
            displayName: user.displayName,
            deleted: false,
            href: '',
          };
        });

        queryClient.setQueryData<DomainItem>(domainKeys.detail(domainFqn), {
          ...previousDomain,
          experts: newExperts,
        });
      }

      return { previousDomain };
    },
    onError: (error, _variables, context) => {
      if (context?.previousDomain) {
        queryClient.setQueryData(domainKeys.detail(domainFqn), context.previousDomain);
      }
      notification.error({
        message: NOTIFICATION_MESSAGES.EXPERT_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.EXPERT_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(domainFqn) });
      notification.success({
        message: NOTIFICATION_MESSAGES.EXPERT_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.EXPERT_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 2,
      });
    },
  });

  const currentUserIds = useMemo(
    () => currentExperts.filter((expert) => expert.type === 'user').map((expert) => expert.id),
    [currentExperts]
  );

  const currentUserIdsString = currentUserIds.join(',');

  useEffect(() => {
    setSelectedUserIds(currentUserIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserIdsString]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestDomainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
    const latestExperts = latestDomainData?.experts || [];

    if (latestExperts.length > 0) {
      const userExpertIds = latestExperts.filter((expert) => expert.type === 'user').map((expert) => expert.id);
      setSelectedUserIds(userExpertIds);
    }
  }, [queryClient, domainFqn]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSelectedUserIds(currentUserIds);
  }, [currentUserIds]);

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => (checked ? [...prev, userId] : prev.filter((id) => id !== userId)));
  }, []);

  const handleUpdate = useCallback(() => {
    const latestDomainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
    const latestExperts = latestDomainData?.experts || [];

    updateExpertsMutation.mutate(
      {
        selectedUserIds,
        users,
        currentExperts: latestExperts,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      }
    );
  }, [selectedUserIds, users, queryClient, domainFqn, updateExpertsMutation]);

  const hasChanges = useMemo(() => {
    const usersChanged =
      currentUserIds.length !== selectedUserIds.length || !currentUserIds.every((id) => selectedUserIds.includes(id));

    return usersChanged;
  }, [currentUserIds, selectedUserIds]);

  return {
    isOpen,
    selectedUserIds,
    users,
    currentExperts,
    isLoadingUsers,
    isUpdating: updateExpertsMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    handleUserSelect,
    handleUpdate,
  };
};
